import { useState, useCallback, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { toastSuccess, toastError } from '../utils/toasts';
import { useCreateOrderMutation } from '../store';
import useSendWhatsapp from './useSendWhatsapp';
import generateWhatsappMessage from '../utils/generateWhatsappMessage';
import { PaymentType, PaymentProvider } from '../models/payment';
import {
  OrderData,
  PayWayTokenData,
  NavePaymentData,
} from '../store/apis/orders';
import { NaveCheckoutResult } from '../components/NaveCheckout';
import EventDTO from '../models/event';
import { OrderItemData } from '../models/order';
import { PollData } from '../models/poll';
import { CheckoutFormData } from '../components/checkout/checkoutValidation';

interface UseCheckoutPaymentProps {
  event: EventDTO;
  orderItemData: OrderItemData[];
  pollData: PollData[];
  reservation?: { code: string } | null;
  isBackoffice: boolean;
  role?: string | null;
  refCode?: string | null;
  skipPoll?: boolean;
  link?: string | null;
  appliedCoupon?: { id: string } | null;
  openPriceAmount?: number;
  totalToPay?: number;
  form: UseFormReturn<CheckoutFormData>;
  agent?: { phone_number?: string } | null;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

interface UseCheckoutPaymentReturn {
  paymentMethod: PaymentType | null;
  setPaymentMethod: (method: PaymentType | null) => void;
  paymentProvider: PaymentProvider | null;
  setPaymentProvider: (provider: PaymentProvider | null) => void;
  showPaymentOptions: boolean;
  clickedPay: boolean;
  isPayWayProcessing: boolean;
  setIsPayWayProcessing: (processing: boolean) => void;
  isNaveProcessing: boolean;
  setIsNaveProcessing: (processing: boolean) => void;
  navePaymentRequestId: string | null;
  payWayTokenData: PayWayTokenData | null;
  orderData: OrderData | null;
  isLoading: boolean;
  handlePay: () => void;
  handlePayWayTokenCreated: (tokenData: PayWayTokenData) => void;
  handlePayWayError: (error: string) => void;
  handleNavePaymentComplete: (result: NaveCheckoutResult) => void;
  handleNaveError: (error: string) => void;
  getPayButtonLabel: () => string;
  getPaymentProvider: () => PaymentProvider | undefined;
}

export const useCheckoutPayment = ({
  event,
  orderItemData,
  pollData,
  reservation,
  isBackoffice,
  role,
  refCode,
  skipPoll,
  link,
  appliedCoupon,
  openPriceAmount,
  totalToPay,
  form,
  agent,
  navigate,
}: UseCheckoutPaymentProps): UseCheckoutPaymentReturn => {
  const [createOrder, results] = useCreateOrderMutation();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [clickedPay, setClickedPay] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentType | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider | null>(null);
  const [payWayTokenData, setPayWayTokenData] = useState<PayWayTokenData | null>(null);
  const [isPayWayProcessing, setIsPayWayProcessing] = useState(false);
  const [, setNavePaymentData] = useState<NavePaymentData | null>(null);
  const [navePaymentRequestId, setNavePaymentRequestId] = useState<string | null>(null);
  const [isNaveProcessing, setIsNaveProcessing] = useState(false);

  // Determine if payment options should be shown
  useEffect(() => {
    setShowPaymentOptions(
      !!(
        event.allow_manual_payments
        && !isBackoffice
        && totalToPay
        && totalToPay > 0
      ),
    );
  }, [event, isBackoffice, totalToPay]);

  // Auto-set payment method to ONLINE when only online payments are available
  useEffect(() => {
    const hasManualOption = event.allow_manual_payments;
    const shouldAutoSelectOnline = !hasManualOption
      && !isBackoffice
      && totalToPay
      && totalToPay > 0;

    if (shouldAutoSelectOnline && paymentMethod !== PaymentType.ONLINE) {
      setPaymentMethod(PaymentType.ONLINE);
    }
  }, [event, isBackoffice, totalToPay, paymentMethod]);

  const getPaymentProvider = useCallback(() => {
    const isManual = paymentMethod === PaymentType.MANUAL || totalToPay === 0 || isBackoffice;
    if (isManual) {
      return PaymentProvider.AVANTTI;
    }
    if (paymentProvider === PaymentProvider.NAVE_LINK) {
      return PaymentProvider.NAVE;
    }
    return paymentProvider ?? undefined;
  }, [isBackoffice, paymentMethod, totalToPay, paymentProvider]);

  const getOrderData = useCallback(
    (
      paywayToken?: PayWayTokenData | null,
      navePayment?: NavePaymentData | null,
    ): OrderData => ({
      email: form.getValues('email') ?? '',
      phone: form.getValues('phone') ?? '',
      items: orderItemData.map((item) => ({
        name: item.name,
        last_name: item.last_name,
        person_id: item.person_id,
        ticket_id: item.ticket.id,
        event_seat_id: item.seat_id,
        audience_discount_id: item.audience_discount_id,
      })),
      coupon_id: appliedCoupon?.id,
      open_price_amount: openPriceAmount,
      poll_responses: pollData,
      is_manual:
        paymentMethod === PaymentType.MANUAL
        || isBackoffice
        || totalToPay === 0,
      skip_poll: skipPoll,
      referral_code: !isBackoffice ? refCode : undefined,
      from_backoffice: isBackoffice,
      is_seller_order: isBackoffice && role === 'SELLER',
      reservation_code: reservation?.code,
      payment_provider: getPaymentProvider(),
      payway_token:
        getPaymentProvider() === PaymentProvider.PAYWAY
          ? paywayToken || undefined
          : undefined,
      nave_payment:
        getPaymentProvider() === PaymentProvider.NAVE
          ? navePayment || undefined
          : undefined,
    }),
    [
      form,
      orderItemData,
      appliedCoupon,
      openPriceAmount,
      pollData,
      paymentMethod,
      isBackoffice,
      totalToPay,
      skipPoll,
      refCode,
      reservation,
      getPaymentProvider,
      role,
    ],
  );

  useEffect(() => {
    setOrderData(getOrderData());
  }, [getOrderData]);

  const processOrder = useCallback(
    (
      tokenData?: PayWayTokenData | null,
      navePayment?: NavePaymentData | null,
    ) => {
      setClickedPay(true);
      const emailValue = form.getValues('email');
      if (emailValue) {
        localStorage.setItem(`${event.id}_email`, emailValue);
      }

      if (
        link
        && paymentMethod === PaymentType.ONLINE
        && paymentProvider !== PaymentProvider.PAYWAY
        && paymentProvider !== PaymentProvider.NAVE
        && paymentProvider !== PaymentProvider.ZILO
      ) {
        window.location.href = link;
        return;
      }

      const action = createOrder({
        order: getOrderData(tokenData, navePayment),
        businessId: event.business_id,
        eventId: event.id,
      });

      action
        .unwrap()
        .then((res) => {
          const od = res.data;
          if (od) {
            if (isBackoffice) {
              toastSuccess('Orden creada');
              return navigate(`/admin/order-success?id=${od.custom_id}`, {
                replace: true,
              });
            }
            if (totalToPay === 0) {
              return navigate(`/order-success?id=${od.custom_id}`, {
                replace: true,
              });
            }
            if (paymentProvider === PaymentProvider.PAYWAY && tokenData) {
              toastSuccess('¡Pago procesado exitosamente!');
              return navigate(`/order-success?id=${od.custom_id}`, {
                replace: true,
              });
            }
            if (
              paymentProvider === PaymentProvider.NAVE
              && navePayment?.status === 'approved'
            ) {
              toastSuccess('¡Pago procesado exitosamente!');
              return navigate(`/order-success?id=${od.custom_id}`, {
                replace: true,
              });
            }
            if (paymentProvider === PaymentProvider.NAVE && !navePayment) {
              const naveRequestId = od.payments?.[0]?.remote_id;
              if (naveRequestId) {
                setNavePaymentRequestId(naveRequestId);
                setClickedPay(false);
                return '';
              }
            }
            if (paymentProvider === PaymentProvider.ZILO) {
              return navigate(`/order-success?id=${od.custom_id}`, {
                replace: true,
              });
            }
            const checkoutLink = od.payments?.[0]?.checkout_link;
            if (!od.is_manual) {
              if (checkoutLink) {
                window.location.href = checkoutLink;
              }
            } else {
              useSendWhatsapp(
                agent?.phone_number || event.phone,
                generateWhatsappMessage(
                  event,
                  od,
                  orderItemData,
                  totalToPay ?? 0,
                ),
              );
              return navigate(`/order-success?id=${od.custom_id}`, {
                replace: true,
              });
            }
          } else {
            throw new Error('no order data');
          }
          return '';
        })
        .catch(async (res) => {
          console.error(res);
          setClickedPay(false);
          setPayWayTokenData(null);
          setNavePaymentData(null);
          setNavePaymentRequestId(null);
          if (res?.data?.error === 'no tickets available to fulfill order') {
            toastError(
              'Lo sentimos, las entradas que habías solicitado se agotaron.',
            );
            return navigate('../tickets', { replace: true });
          }
          localStorage.removeItem(`${event.id}_orderItemData`);
          localStorage.removeItem(`${event.id}_selectedTickets`);
          localStorage.removeItem(`${event.id}_coupon`);
          toastError(
            'Se produjo un error al crear la orden. Intenta nuevamente',
          );
          return '';
        });
    },
    [
      agent,
      createOrder,
      event,
      form,
      getOrderData,
      isBackoffice,
      link,
      navigate,
      orderItemData,
      paymentMethod,
      paymentProvider,
      totalToPay,
    ],
  );

  const handlePay = () => {
    if (!paymentMethod && totalToPay && totalToPay > 0 && !isBackoffice) {
      toastError('Por favor seleccioná un método de pago');
      return;
    }
    if (
      paymentMethod === PaymentType.ONLINE
      && totalToPay
      && totalToPay > 0
      && !isBackoffice
      && !paymentProvider
    ) {
      toastError('Por favor seleccioná un medio de pago');
      return;
    }
    if (
      paymentProvider === PaymentProvider.PAYWAY
      && paymentMethod === PaymentType.ONLINE
      && totalToPay
      && totalToPay > 0
    ) {
      if (!payWayTokenData) {
        toastError('Por favor completá los datos de la tarjeta');
        return;
      }
      processOrder(payWayTokenData);
      return;
    }
    if (
      paymentProvider === PaymentProvider.NAVE
      && paymentMethod === PaymentType.ONLINE
      && totalToPay
      && totalToPay > 0
    ) {
      if (navePaymentRequestId) {
        return;
      }
      processOrder();
      return;
    }
    processOrder();
  };

  const handlePayWayTokenCreated = (tokenData: PayWayTokenData) => {
    setPayWayTokenData(tokenData);
    processOrder(tokenData);
  };

  const handlePayWayError = (error: string) => {
    toastError(error);
    setClickedPay(false);
  };

  const handleNavePaymentComplete = (result: NaveCheckoutResult) => {
    if (result.success && result.status === 'approved') {
      const navePayment: NavePaymentData = {
        payment_request_id: navePaymentRequestId || '',
        status: result.status,
        payment_id: result.paymentId,
      };
      setNavePaymentData(navePayment);
      processOrder(null, navePayment);
    } else if (result.status === 'pending') {
      toastError(result.message || 'El pago está pendiente de confirmación');
      setClickedPay(false);
    } else {
      toastError(result.message || 'El pago no pudo ser procesado');
      setClickedPay(false);
      setNavePaymentRequestId(null);
    }
  };

  const handleNaveError = (error: string) => {
    toastError(error);
    setClickedPay(false);
    setNavePaymentRequestId(null);
  };

  const getPayButtonLabel = () => {
    if (isBackoffice || totalToPay === 0) {
      return 'Confirmar';
    }
    if (paymentMethod === PaymentType.ONLINE) {
      return 'Pagar';
    }
    if (paymentMethod === PaymentType.MANUAL) {
      return 'Solicitar entradas';
    }
    return 'Confirmar';
  };

  return {
    paymentMethod,
    setPaymentMethod,
    paymentProvider,
    setPaymentProvider,
    showPaymentOptions,
    clickedPay,
    isPayWayProcessing,
    setIsPayWayProcessing,
    isNaveProcessing,
    setIsNaveProcessing,
    navePaymentRequestId,
    payWayTokenData,
    orderData,
    isLoading: results.isLoading,
    handlePay,
    handlePayWayTokenCreated,
    handlePayWayError,
    handleNavePaymentComplete,
    handleNaveError,
    getPayButtonLabel,
    getPaymentProvider,
  };
};

export default useCheckoutPayment;
